import React from 'react';
import round from 'lodash/round';
import maxBy from 'lodash/maxBy';
import GeneralChart from '../GeneralChart';
import InfoTooltip from '../InfoTooltip';
import { CusEcharts } from '@/components/AsyncComponent';
import type { ECOption } from '@/components/CusEcharts';
import { useClintMediaQuery } from '@/utils/hooks/useClintMediaQuery';
import { CityContext } from '@/context/create-context';
import {
  WORK_EXPERIENCE_1_3,
  WORK_EXPERIENCE_CITY_MAP,
} from '@/constants/job-title';
import { handleEchartsEvent } from '@/utils/common-utils';

const DEFAULT_COLORS = [
  '#ff9f40',
  '#fc8452',
  '#5388d8',
  '#f4be37',
  '#0284c7',
  '#0d2535',
];

const WorkDistribution = () => {
  const chartRef = React.useRef(null);
  const { name: cityName, experience_distributed: experienceDistributed } =
    React.useContext(CityContext);
  const isMobile = useClintMediaQuery();

  const config = React.useMemo<ECOption>(() => {
    const experienceKeys = Object.keys(experienceDistributed);
    const hasDefaultKey = experienceKeys.includes(WORK_EXPERIENCE_1_3);
    const totalCount = Object.values(experienceDistributed).reduce(
      (a, b) => a + b,
      0,
    );
    const data: any[] = Object.entries(WORK_EXPERIENCE_CITY_MAP)
      .map(([key, item]) => {
        const salaryRange = Reflect.get(experienceDistributed, key);
        if (!salaryRange) {
          return;
        }
        return {
          name: item,
          key: key,
          value: experienceDistributed[key],
          label: {
            show:
              key === WORK_EXPERIENCE_1_3 ||
              (!hasDefaultKey && key === experienceKeys[0]),
          },
        };
      })
      .filter(Boolean);

    const maxByData = maxBy(data, 'value');
    const seoInfo = `截止当前数据显示，【${cityName}】招聘市场近${round((maxByData.value / totalCount) * 100)}%向【${maxByData.name}】敞开。`;

    return {
      legend: {
        type: 'scroll',
        orient: 'horizontal',
        selectorPosition: 'end',
        bottom: isMobile ? '6%' : 0,
        left: 'center',
        padding: 0,
        itemGap: 20,
        icon: 'circle',
        itemHeight: 8,
        itemWidth: 16,
        selectedMode: false,
        textStyle: {
          fontWeight: 500,
          fontSize: 14,
          lineHeight: 18,
          backgroundColor: 'transparent',
          textBorderWidth: 0,
        },
        formatter: (name) => {
          const value = data.find((item) => item.name === name).value;
          return `\n${name}\n${round((value / totalCount) * 100)}%`;
        },
      },
      color: DEFAULT_COLORS,
      series: [
        {
          type: 'pie',
          avoidLabelOverlap: true,
          label: {
            position: isMobile ? 'inside' : 'outside',
            backgroundColor: isMobile ? '#fff' : 'transparent',
            borderRadius: isMobile ? 8 : 0,
            padding: isMobile ? [8, 8, 5, 8] : 0,
            shadowBlur: isMobile ? 5 : 0,
            shadowColor: isMobile ? 'rgba(0, 0, 0, 0.05)' : '',
            shadowOffsetX: 0,
            shadowOffsetY: isMobile ? 10 : 0,
            color: '#000',
            alignTo: 'none',
            bleedMargin: 5,
            fontSize: 14,
            lineHeight: 22,
            formatter: (params) => {
              const { name, value } = params.data as any;
              const rateValue = round((value / totalCount) * 100);
              if (isMobile) {
                return `{name|${name}} {value|${rateValue}%}`;
              }
              return (
                '{icon' +
                params.dataIndex +
                '|●}' +
                `{name|${name}}` +
                '\n' +
                `{value|${rateValue}%}` +
                ''
              );
            },
            rich: {
              salary: { fontWeight: 'bold', fontSize: 14, lineHeight: 22 },
              icon0: {
                color: DEFAULT_COLORS[0],
                fontSize: 16,
                borderRadius: 4,
                padding: [0, 6, 0, 0],
              },
              icon1: {
                color: DEFAULT_COLORS[1],
                fontSize: 16,
                borderRadius: 4,
                padding: [0, 6, 0, 0],
              },
              icon2: {
                color: DEFAULT_COLORS[2],
                fontSize: 16,
                borderRadius: 4,
                padding: [0, 6, 0, 0],
              },
              icon3: {
                color: DEFAULT_COLORS[3],
                fontSize: 16,
                borderRadius: 4,
                padding: [0, 6, 0, 0],
              },
              icon4: {
                color: DEFAULT_COLORS[4],
                fontSize: 16,
                borderRadius: 4,
                padding: [0, 6, 0, 0],
              },
              icon5: {
                color: DEFAULT_COLORS[5],
                fontSize: 16,
                borderRadius: 4,
                padding: [0, 6, 0, 0],
              },
            },
          },
          bottom: 40,
          labelLine: {
            show: true,
            showAbove: true,
          },
          cursor: 'default',
          emphasis: { disabled: true },
          data,
        },
      ],
      seoInfo,
    };
  }, [experienceDistributed, isMobile]);

  return (
    <GeneralChart
      title="经验要求分布"
      subTip="揭示市场对不同经验层次人才的需求结构，助您找准自身定位。"
    >
      <div className="relative w-full h-[310px]">
        <CusEcharts
          chartRef={chartRef}
          config={config}
          handleEvent={handleEchartsEvent}
        />
      </div>
      <p className="flex mt-4">
        <InfoTooltip className="mt-[5px] mr-1 shrink-0" />
        {config.seoInfo}
      </p>
    </GeneralChart>
  );
};

export default WorkDistribution;
